import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { AuthProvider } from "@/context/AuthContext";
import { PermissionsProvider } from "@/context/PermissionsContext";
import { WebSocketProvider } from "@/context/WebSocketContext";
import { ShareMilestoneProvider } from "@/components/sharing";
import { Toaster } from "@/components/ui/sonner";
import { PWAInstallPrompt } from "@/components/pwa/PWAInstallPrompt";
import { 
  ProtectedRoute, 
  OrgRoleGuard, 
  PlatformRoleGuard, 
  AuthRouteGuard,
  StudentRouteGuard 
} from "@/components/auth/RouteGuards";
import { PathGuard } from "@/components/auth/PermissionGuards";

// Layouts
import { OrgLayout, PlatformLayout, StudentLayout, AuthLayout } from "@/components/layout";

// Pages - Organization
import { CommandCenter, Events, Guests, Scanner, Members, OrgSettings, Billing, Monetization, OrgSponsors, Campaigns, OrgProfile, MyTicket, MyTickets, DoorStaffScanner, DoorStaffManagement, Dues, Documents, Announcements, Recruitment, Analytics, AlumniDirectory, PermissionSettings, ChapterSettings, DocumentTemplates } from "@/pages/org";

// Pages - Platform
import { Dashboard, Campuses, Organizations, PlatformEvents, Revenue, Users, Presidents, Support, PlatformSettings, Sponsors, Advertisements, AdPlacements, AdAnalytics, RevenueManagement, PlatformMonetization, PlatformProfile } from "@/pages/platform";

// Pages - Student
import { Discover, MyPass, Passport, Leaderboards, StudentBilling } from "@/pages/student";

// Pages - Auth
import { Login, Signup, ForgotPassword } from "@/pages/auth";

// Pages - Shared
import Help from "@/pages/shared/Help";
import ClaimOrganization from "@/pages/shared/ClaimOrganization";
import { AccessDenied as AccessDeniedPage } from "@/pages/shared/AccessDenied";
import AgeVerification from "@/pages/shared/AgeVerification";
import VerifyCallback from "@/pages/shared/VerifyCallback";

// Pages - Onboarding
import { Onboarding } from "@/pages/onboarding";

// Pages - Admin
import { PlatformAdmin } from "@/pages/admin";

// Landing
import Landing from "@/pages/Landing";

// Invite Landing
import InviteLanding from "@/pages/InviteLanding";

// Dev Tools
import ComponentLibrary from "@/pages/dev/ComponentLibrary";

// Shared
import AccessDenied from "@/components/shared/AccessDenied";

// Accept Invite
import AcceptInvite from "@/pages/AcceptInvite";

function App() {
  // Register service worker for PWA
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      window.addEventListener('load', () => {
        navigator.serviceWorker.register('/service-worker.js')
          .then((registration) => {
            console.log('SW registered:', registration.scope);
          })
          .catch((error) => {
            console.log('SW registration failed:', error);
          });
      });
    }
  }, []);

  return (
    <AuthProvider>
      <PermissionsProvider>
        <WebSocketProvider>
          <ShareMilestoneProvider>
            <div className="App">
              <BrowserRouter>
                <Routes>
                {/* Public Routes */}
                <Route path="/" element={<Landing />} />
                <Route path="/dev/components" element={<ComponentLibrary />} />
                
                {/* Access Denied Route */}
                <Route path="/access-denied" element={<AccessDeniedPage />} />

                {/* Auth Routes - redirect if logged in */}
                <Route element={<AuthLayout />}>
                  <Route path="/login" element={
                    <AuthRouteGuard>
                      <Login />
                    </AuthRouteGuard>
                  } />
              <Route path="/signup" element={
                <AuthRouteGuard>
                  <Signup />
                </AuthRouteGuard>
              } />
              <Route path="/forgot-password" element={
                <AuthRouteGuard>
                  <ForgotPassword />
                </AuthRouteGuard>
              } />
            </Route>

            {/* Onboarding - requires auth but no org yet */}
            <Route path="/onboarding" element={
              <ProtectedRoute>
                <Onboarding />
              </ProtectedRoute>
            } />

            {/* Accept Invite - public route but processes after login */}
            <Route path="/invite/accept" element={<AcceptInvite />} />
            
            {/* Claim Organization - public page for users to claim orgs */}
            <Route path="/claim-organization" element={<ClaimOrganization />} />
            
            {/* Age Verification Flow - requires auth */}
            <Route path="/verify/age" element={
              <ProtectedRoute>
                <AgeVerification />
              </ProtectedRoute>
            } />
            <Route path="/verify/callback" element={
              <ProtectedRoute>
                <VerifyCallback />
              </ProtectedRoute>
            } />
            
            {/* Invite Landing - public route for referral links */}
            <Route path="/invite/:code" element={<InviteLanding />} />

            {/* Organization Routes - require org role */}
            <Route path="/app" element={
              <OrgRoleGuard>
                <OrgLayout />
              </OrgRoleGuard>
            }>
              <Route index element={<Navigate to="/app/command-center" replace />} />
              <Route path="command-center" element={<CommandCenter />} />
              <Route path="events" element={<Events />} />
              <Route path="guests" element={<Guests />} />
              <Route path="scanner" element={<Scanner />} />
              <Route path="members" element={<Members />} />
              <Route path="settings" element={<OrgSettings />} />
              <Route path="profile" element={<OrgProfile />} />
              <Route path="billing" element={<Billing />} />
              <Route path="monetization" element={<Monetization />} />
              <Route path="sponsors" element={<OrgSponsors />} />
              <Route path="campaigns" element={<Campaigns />} />
              <Route path="dues" element={<Dues />} />
              <Route path="documents" element={<Documents />} />
              <Route path="documents/templates" element={<DocumentTemplates />} />
              <Route path="announcements" element={<Announcements />} />
              <Route path="recruitment" element={<Recruitment />} />
              <Route path="analytics" element={<Analytics />} />
              <Route path="events/:eventId/door-staff" element={<DoorStaffManagement />} />
              <Route path="alumni" element={<AlumniDirectory />} />
              <Route path="permission-settings" element={<PermissionSettings />} />
              <Route path="chapter-settings" element={<ChapterSettings />} />
              <Route path="my-tickets" element={<MyTickets />} />
              <Route path="my-ticket/:passId" element={<MyTicket />} />
              <Route path="help" element={<Help />} />
            </Route>
            
            {/* Door Staff Scanner - Full screen, no layout */}
            <Route path="/door-staff" element={
              <OrgRoleGuard>
                <DoorStaffScanner />
              </OrgRoleGuard>
            } />

            {/* Platform Admin Routes - require platform role */}
            <Route path="/platform" element={
              <PlatformRoleGuard>
                <PlatformLayout />
              </PlatformRoleGuard>
            }>
              <Route index element={<Dashboard />} />
              <Route path="campuses" element={<Campuses />} />
              <Route path="organizations" element={<Organizations />} />
              <Route path="events" element={<PlatformEvents />} />
              <Route path="revenue" element={<Revenue />} />
              <Route path="users" element={<Users />} />
              <Route path="presidents" element={<Presidents />} />
              <Route path="support" element={<Support />} />
              <Route path="settings" element={<PlatformSettings />} />
              <Route path="profile" element={<PlatformProfile />} />
              <Route path="sponsors" element={<Sponsors />} />
              <Route path="ads" element={<Advertisements />} />
              <Route path="placements" element={<AdPlacements />} />
              <Route path="analytics" element={<AdAnalytics />} />
              <Route path="monetization" element={<PlatformMonetization />} />
              <Route path="revenue-management" element={<RevenueManagement />} />
              <Route path="help" element={<Help />} />
            </Route>

            {/* Student Routes - require any authenticated user */}
            <Route element={
              <StudentRouteGuard>
                <StudentLayout />
              </StudentRouteGuard>
            }>
              <Route path="/my-pass" element={<MyPass />} />
              <Route path="/passport" element={<Passport />} />
              <Route path="/leaderboards" element={<Leaderboards />} />
              <Route path="/student/billing" element={<StudentBilling />} />
            </Route>

            {/* Discover - public page with StudentLayout */}
            <Route element={<StudentLayout />}>
              <Route path="/discover" element={<Discover />} />
            </Route>

            {/* Platform Admin Dashboard - requires platform_owner or platform_admin role */}
            <Route path="/admin" element={
              <ProtectedRoute>
                <PlatformAdmin />
              </ProtectedRoute>
            } />

            {/* Access Denied standalone page */}
            <Route path="/access-denied" element={
              <div className="min-h-screen bg-obsidian">
                <AccessDenied />
              </div>
            } />

            {/* Catch all - redirect to landing */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster richColors position="top-right" />
        <PWAInstallPrompt />
      </div>
      </ShareMilestoneProvider>
      </WebSocketProvider>
      </PermissionsProvider>
    </AuthProvider>
  );
}

export default App;
